import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Sidebar as ShadCnSideBar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import UserAvatar from "@/components/shared/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LogOut, ChevronRight, GitCompareArrows } from "lucide-react";
import { navItems } from "./routes";
import { cn } from "@/lib/utils";
import { logout, selectUser } from "@/features/auth/authSlice";
import dgrLogo from "@/assets/logoXRBlack.png";
import faqLogo from "@/assets/LogoUniversiteSaidaBlack.png";
import dgrLogoDark from "@/assets/LogoXRBig.png";
import faqLogoDark from "@/assets/LogoUniversiteSaida.png";
import tachrihLogo from "@/assets/LogoTechrihBL.png";
import tachrihLogoDark from "@/assets/LogoTechrih 1.png";

import { useTheme } from "./themeProvider";

const Sidebar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { theme } = useTheme();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/", { replace: true });
  };

  const handleNavigation = (link) => {
    navigate(link);
  };

  const filteredNavItems = navItems.filter((item) => {
    if (!user?.role) return false;
    return item.roles.includes(user.role);
  });

  const isMenuItemActive = (item) => {
    if (
      pathname === item.link ||
      (item.link === "/dashboard" && pathname === "/dashboard")
    ) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) => pathname === child.link);
    }
    return false;
  };

  const shouldBeOpen = (item) => {
    if (item.children) {
      return item.children.some((child) => pathname === child.link);
    }
    return false;
  };

  const filterChildren = (children) => {
    if (!children) return [];
    return children.filter((child) => {
      if (!user?.role) return false;
      return child.roles.includes(user.role);
    });
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isActive = isMenuItemActive(item);
    const filteredChildren = filterChildren(item.children);
    const hasChildren = filteredChildren.length > 0;

    if (hasChildren) {
      return (
        <Collapsible
          key={item.text}
          defaultOpen={shouldBeOpen(item)}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                isActive={isActive}
                className="w-full cursor-pointer "
              >
                <Icon className="h-4 w-4" />
                <span>{item.text}</span>
                <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            {!isCollapsed && (
              <CollapsibleContent
                className={cn(
                  "text-popover-foreground outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                )}
              >
                <SidebarMenuSub>
                  {filteredChildren.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = pathname === child.link;
                    return (
                      <SidebarMenuSubItem key={child.text}>
                        <SidebarMenuSubButton
                          className="cursor-pointer"
                          onClick={() => handleNavigation(child.link)}
                          isActive={isChildActive}
                        >
                          <ChildIcon className="h-4 w-4" />
                          <span>{child.text}</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                </SidebarMenuSub>
              </CollapsibleContent>
            )}
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuItem key={item.text}>
        <SidebarMenuButton
          className="cursor-pointer"
          onClick={() => handleNavigation(item.link)}
          isActive={isActive}
        >
          <Icon className="h-4 w-4" />
          <span>{item.text}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <ShadCnSideBar collapsible="icon">
      <SidebarHeader>
        <div
          className={cn(
            "flex items-center gap-2  py-1",
            isCollapsed ? "px-0" : "px-2"
          )}
        >
          <div className="flex  items-center justify-center rounded-lg">
            <img
              src={theme === "dark" ? tachrihLogoDark : tachrihLogo}
              alt="FAQ Logo"
              className="h-14 w-auto object-contain transition-all duration-300"
            />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-semibold font-mono">TechRIH</span>
              <span className="text-lg font-semibold font-mono">
                Ressources
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{filteredNavItems.map(renderMenuItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {!isCollapsed && (
          <div
            className={cn(
              "flex flex-col items-center gap-2 w-full py-4 border-b border-muted",
              isCollapsed ? "hidden" : "px-2"
            )}
          >
            <img
              src={theme === "dark" ? dgrLogoDark : dgrLogo}
              alt="DGR Logo"
              className="h-14 w-auto object- transition-all duration-300 mb-1"
            />
            <span className="text-xs text-muted-foreground font-semibold mt-2">
              powered by
            </span>
            <img
              src={theme === "dark" ? faqLogoDark : faqLogo}
              alt="FAQ Logo"
              className="h-16 w-auto object-contain transition-all duration-300"
            />
          </div>
        )}
        {user && (
          <div
            className={`flex items-center gap-3 px-2 py-1 ${
              isCollapsed ? "justify-center" : ""
            }`}
          >
            <UserAvatar user={user} size="h-8 w-8" showFullName={true} />
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user.name || user.email}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user.role}
                </p>
              </div>
            )}
          </div>
        )}

        <Button
          variant="ghost"
          className={`${
            isCollapsed ? "w-auto p-2 justify-center" : "w-full justify-start"
          } text-red-600 hover:text-red-700 hover:bg-red-50`}
          onClick={handleLogout}
        >
          <LogOut className={`h-4 w-4 ${!isCollapsed ? "mr-2" : ""}`} />
          {!isCollapsed && <span>Sign Out</span>}
        </Button>
      </SidebarFooter>
    </ShadCnSideBar>
  );
};

export default Sidebar;
